<template lang="html">
  <div>
    <div class="mt-5 ml-4 mr-5">
      <vue-slider
        v-model="time_per_image_input"
        :min="min_time_per_image"
        :max="max_time_per_image"
        :interval="0.1"
        tooltip="always"
        :tooltip-formatter="time_per_formatter"
        :lazy="true"
        :height="8"
        :dot-size="20"
        class="mr-3 mb-4"
        :adsorb="true"
        :marks="sliderMarks"
        :tooltip-style="tooltipStyle"
      />
      <p v-if="spaceFeedback === 'tooMany'" class="smaller-1">
        <!-- Too many images for this timing -->
        With even spacing set to {{ time_per_image_input.toFixed(1) }} seconds,
        you have
        <span
          v-b-popover.hover.bottom="spacePopoverFeedback"
          class="space-feedback"
        >
          <strong>{{ trimmed_image_count }}</strong> images
        </span>
        on your timeline. Drag the slider to adjust the amount of time per
        image.
      </p>
      <p v-if="spaceFeedback === 'allFit'" class="smaller-1">
        <!-- All images work on this timing -->
        With even spacing set to {{ time_per_image_input.toFixed(1) }} seconds,
        all <strong>{{ trimmed_image_count }}</strong> images fit on your
        timeline. Drag the slider to adjust the amount of time per image.
      </p>
      <p v-if="spaceFeedback === 'tooFew'" class="smaller-1">
        <!-- Space for more images with this timing -->
        With even spacing set to {{ time_per_image_input.toFixed(1) }} seconds,
        you have room for
        <span
          v-b-popover.hover.bottom="spacePopoverFeedback"
          class="space-feedback"
        >
          <strong>{{ trimmed_image_count }}</strong> images
        </span>
        on your timeline. Drag the slider to adjust the amount of time per
        image.
      </p>

      <p v-if="spaceFeedback === 'tooFew'" class="smaller-1">
        <!-- Space for more images with this timing -->
        <b-form-checkbox
          id="auto_trim_audio-checkbox-1"
          v-model="auto_trim_audio"
          name="auto_trim_audio-checkbox-1"
          :value="true"
          :unchecked-value="false"
        >
          Auto-trim audio file to match timing and number of images.
        </b-form-checkbox>
        <span class="auto-trim-subline">
          Enable auto-trim to fade out your audio file at
          {{ (auto_trim_secs * 1000) | toMMSS }}.
        </span>
      </p>

      <p class="mb-1 smaller-1 mt-4">Default slide transition</p>
      <b-form-select
        id="select-1"
        v-model="transition_selected"
        size="md"
        :options="transition_options"
        class="transition-form"
      />
      <div v-if="$store.state.user_doc.beta_tester">
        <p class="mb-1 smaller-1 mt-2">
          For individual slide transitions, open in Timeline Editor.
        </p>
        <p class="mb-1 smaller-1 mt-4">Default slide motion</p>
        <b-form-select
          id="select-1"
          v-model="motion_selected"
          size="md"
          :options="motion_options"
          class="transition-form"
        />
        <p class="mb-1 smaller-1 mt-3">
          <span class="p-2 mark">
            <v-icon name="flask" class="mr-1" /> Beta Feature: Slide motion
            limited to video export only.
          </span>
        </p>
      </div>
      <b-row class="mt-4">
        <b-col>
          <b-img
            :src="require('./timeline.png')"
            thumbnail
            class="timeline-thumb float-left"
          />
          <b-btn
            size="md"
            variant="primary"
            class="float-left ml-3 mt-2"
            @click="
              $store.dispatch('show/createInitialSlideArrayAndOpenEditor')
            "
          >
            Open in Timeline Editor <v-icon name="magic" />
          </b-btn>
        </b-col>
      </b-row>
      <p class="smaller-1 mt-2">
        Need more control of image timings and transitions? Overlays?
        <br />Open the Timeline Editor.
      </p>
    </div>
  </div>
</template>

<script>
import find from "lodash/find";
var toolTipColor = "#495057";

export default {
  name: "ShowTimelineSetup",
  components: {},
  filters: {
    toMMSS: function(s) {
      // Pad to 2 or 3 digits, default is 2
      function pad(n, z) {
        z = z || 2;
        return ("00" + n).slice(-z);
      }

      var ms = s % 1000;
      s = (s - ms) / 1000;
      var secs = s % 60;
      s = (s - secs) / 60;
      var mins = s % 60;

      return pad(mins) + ":" + pad(secs);
    }
  },
  data() {
    return {
      time_per_image_input: 3,
      min_time_per_image: 1,
      motion_selected: "none",
      motion_options: [
        {
          value: "none",
          text: "No motion",
          name: "none",
          speed: 1500
        },
        {
          value: "random-slow",
          text: "Random direction - Slow",
          name: "random",
          speed: 13 // Percent per second
        },
        {
          value: "random-medium",
          text: "Random direction - Medium",
          name: "random",
          speed: 80
        },
        {
          value: "random-fast",
          text: "Random direction - Fast",
          name: "random",
          speed: 200
        },
        {
          value: "alternate-slow",
          text: "Alternate direction - Slow",
          name: "alternate",
          speed: 13
        },
        {
          value: "alternate-medium",
          text: "Alternate direction - Medium",
          name: "alternate",
          speed: 80
        },
        {
          value: "alternate-fast",
          text: "Alternate direction - Fast",
          name: "alternate",
          speed: 200
        }
      ],
      tooltipStyle: {
        "border-color": toolTipColor,
        "background-color": toolTipColor,
        color: "white",
        "font-size": "13px"
      },
      transition_selected: "cf-medium",
      transition_options: [
        {
          value: "cf-slow",
          text: "Crossfade - Slow",
          name: "crossfade",
          time_ms: 1500
        },
        {
          value: "cf-medium",
          text: "Crossfade - Medium",
          name: "crossfade",
          time_ms: 750
        },
        {
          value: "cf-fast",
          text: "Crossfade - Fast",
          name: "crossfade",
          time_ms: 300
        },
        {
          value: "straight-cut",
          text: "Straight cut",
          name: "straight cut",
          time_ms: 0
        },
        {
          value: "fofi-slow",
          text: "Fade out/Fade in - Slow",
          name: "fade out/fade in",
          time_ms: 1500
        },
        {
          value: "fofi-medium",
          text: "Fade out/Fade in - Medium",
          name: "fade out/fade in",
          time_ms: 750
        },
        {
          value: "fofi-fast",
          text: "Fade out/Fade in - Fast",
          name: "fade out/fade in",
          time_ms: 300
        }
      ]
    };
  },
  computed: {
    show() {
      return this.$store.state.show.currentShow;
    },
    show_audio() {
      if (this.show.data) {
        return this.show.data.audio;
      }
      return null;
    },
    auto_trim_secs() {
      return (
        this.time_per_image_input *
        this.$store.state.show.currentShow.data.images.length
      );
    },
    auto_trim_audio: {
      get() {
        return (
          this.show.data.displaySettings.audioDurationOverrideEnabled == true
        );
      },
      set(value) {
        console.log("set", value);
        this.$store.dispatch("show/updateAudioOverrideEnabled", value);
      }
    },
    spaceFeedback() {
      var numImages = this.$store.state.show.currentShow.data.images.length;
      if (this.trimmed_image_count === numImages) {
        return "allFit";
      }
      if (this.trimmed_image_count < numImages) {
        return "tooMany";
      }
      if (this.trimmed_image_count > numImages) {
        return "tooFew";
      }
      return "allFit";
    },
    spacePopoverFeedback() {
      if (this.spaceFeedback === "tooMany") {
        var extraImages =
          this.$store.state.show.currentShow.data.images.length -
          this.trimmed_image_count;
        var imagePlural =
          extraImages > 1
            ? `There are ${extraImages} images left out.`
            : `There is 1 image left out.`;
        return (
          "With these settings, you have more images than time available on your timeline. " +
          `${imagePlural} To include all images at these settings you'll need a longer audio file.`
        );
      }
      if (this.spaceFeedback === "tooFew") {
        var additionalImages =
          this.trimmed_image_count -
          this.$store.state.show.currentShow.data.images.length;
        var additionalImagePlural = additionalImages > 1 ? "images" : "image";
        return (
          "You can fit more images on your timeline with your current setting. " +
          `Consider adding ${additionalImages} more ${additionalImagePlural}, or enable auto-trim (below) to shorten your audio file.`
        );
      }
      return "";
    },
    max_time_per_image() {
      var max_default = 20;
      console.log("this.all_images_spread_value", this.all_images_spread_value);
      if (
        this.all_images_spread_value > max_default &&
        isFinite(this.all_images_spread_value)
      ) {
        // Return 4 seconds beyond the max.
        return Math.round((this.all_images_spread_value + 4) * 10) / 10;
      } else {
        return max_default;
      }
    },
    trimmed_image_count() {
      return Math.ceil(
        this.show.data.audio.duration / this.show.data.autoTimePerImage
      );
    },
    all_images_spread_value() {
      var numImages = this.$store.state.show.currentShow.data.images.length;
      var all_images_spread_value = this.show_audio.duration / numImages;
      if (isFinite(all_images_spread_value)) {
        // In most cases, this value can be calculated.
        return all_images_spread_value;
      } else {
        // Use a reasonable default when all of the user's files failed to upload
        // (or user deleted them all)
        return 3;
      }
    },
    sliderMarks() {
      var numImages = this.$store.state.show.currentShow.data.images.length;
      return { [this.all_images_spread_value]: `Use all ${numImages} images` };
    }
  },
  watch: {
    time_per_image_input(value) {
      if (value == this.$store.state.show.currentShow.data.autoTimePerImage) {
        return;
      }
      this.$store.dispatch("show/updateAutoTime", value);
    },
    transition_selected(value) {
      if (
        value ==
        this.$store.state.show.currentShow.data.displaySettings
          .globalTransitionPreset
      ) {
        return;
      }
      var transition = find(this.transition_options, { value: value });
      var update = {
        globalTransitionTime: transition.time_ms / 1000,
        globalTransitionType: transition.name,
        globalTransitionPreset: transition.value
      };
      this.$store.dispatch("show/updateGlobalTransitions", update);
    },
    motion_selected(value) {
      if (
        value ==
        this.$store.state.show.currentShow.data.displaySettings
          .globalMotionPreset
      ) {
        return;
      }
      var motion = find(this.motion_options, { value: value });
      var update = {
        globalMotionSpeed: motion.speed,
        globalMotionType: motion.name,
        globalMotionPreset: motion.value
      };
      this.$store.dispatch("show/updateGlobalMotion", update);
    }
  },
  mounted() {
    var displaySettings =
      this.$store.state.show.currentShow.data.displaySettings || {};
    this.time_per_image_input =
      this.$store.state.show.currentShow.data.autoTimePerImage ||
      this.all_images_spread_value;
    this.transition_selected =
      displaySettings.globalTransitionPreset || "cf-medium";
    this.motion_selected = displaySettings.globalMotionPreset || "none";
    if (
      !displaySettings.audioDurationOverride ||
      displaySettings.audioDurationOverride == 0
    ) {
      this.auto_trim_audio = false;
    } else {
      this.auto_trim_audio = true;
    }
  },
  methods: {
    time_per_formatter(value) {
      return `${value.toFixed(1)} secs per image`;
    }
  }
};
</script>

<style lang="scss" scoped>
.smaller-1 {
  font-size: 0.84em;
}
.auto-trim-subline {
  font-size: 12px;
}
.timeline-thumb {
  max-height: 60px;
}
.transition-form {
  max-width: 350px;
}
.space-feedback {
  border-bottom: 1px dotted #222;
  padding-bottom: 1px;
}
</style>
